import { PSBErrors } from "@/constants/errors/product-slot-booking";
import {
  AdyenResponseStatus,
  IAdyenResponseStatusTrack,
  IBookingDiscountTrack,
  IBookingPaymentTrack,
  IBookingsPendingSync,
  IBookingsSteps,
  IBookingState,
  IPromocodeTrack,
  BookingChangesTrack,
  RefundListenerTrack,
} from "@/interfaces/store/IBooking";
import {
  IProductSlotBooking,
  BaseOrderSession,
} from "@/types/db/product-slot-booking";
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "..";
import { BookingStep } from "@/constants/booking";
import {
  AccountingState,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import { Currency } from "@/constants/currency";
import { LogLevel, LogService } from "@/services/logging";
import dayjs from "dayjs";
import { sumArray } from "@/utils/helpers/common";
import { OrderSessionState } from "@smeetz/order-common";

@Module({ dynamic: true, namespaced: true, store, name: "Booking" })
class BookingStoreModule extends VuexModule implements IBookingState {
  public bookings: IBookingState["bookings"] = {};
  public bookingList: IBookingState["bookingList"] = [];
  public isTabAnimated = false;
  // public bookingStep: BookingStep = BookingStep.Order;
  public bookingsSteps: IBookingsSteps = {};
  // public isLoading = false;
  public selectedBookingUUID = "";
  public bookingsAmountsTrack: IBookingPaymentTrack = {};
  public bookingsDiscountsTrack: IBookingDiscountTrack = {};
  // public payingCurrency = Currencies.CHF;
  // The below array keeps the relationship between a promocode and a booking, used to calculate the discount for the UI and for the moment we create accountings
  // We add one item when a promocode is valid for a given booking
  // We remove one item when the booking is completed/cancelled
  public promocodes: IPromocodeTrack[] = [];
  // The below will contian pending bookings synched to backend, they are copies of what backend has. We will need them to build Patch synchronize-bookings payload
  // the whole scenario is needed when a booking has a promocode applied or when we're editing an order that we pulled from the backend
  //
  public adyenResponseStatusTrack: IAdyenResponseStatusTrack = {};

  public bookingsPendingSynchronization: IBookingsPendingSync = {};

  public calculatorValue = "0";

  public selectedCashCurrency: Currency | null = null;

  public currentOrderSessionId: string | null = null;

  public recentTabNames: string[] = [];

  public currentBookingCount = 0;

  public promocodeBeingChecked = false;

  public refundListeners: RefundListenerTrack = {};

  /**
   * Ticket removals / quantity decreases per booking, reported to the Belgian
   * FDM in the sale event. Only written when Belgian compliance is active.
   */
  public bookingChanges: BookingChangesTrack = {};

  logGettersPerformance = false;

  public get getCalculatorValue(): string {
    return this.calculatorValue;
  }

  get selectedBooking(): IProductSlotBooking | null {
    return this.bookings[this.selectedBookingUUID] || null;
  }

  get bookingStep(): BookingStep {
    const step =
      this.selectedBooking?.step ??
      this.bookingsSteps[this.selectedBookingUUID];
    if (!step) {
      LogService.info(
        `L3-5844 BookingModule: No booking step found for UUID ${this.selectedBookingUUID}`,
        {
          selectedBookingUUID: this.selectedBookingUUID,
          bookingsSteps: this.bookingsSteps,
        },
        LogLevel.Error
      );

      // Fallback to Order step if no step is found
      return BookingStep.Order;
    }
    return step;
  }

  /**
   * Returns the confirmed amounts of the booking that is going to be paid
   */
  get confirmedAmounts() {
    return this.bookingsAmountsTrack[this.selectedBookingUUID]
      ? this.bookingsAmountsTrack[this.selectedBookingUUID].confirmedAmounts
      : [];
  }

  /**
   * Total Belgian cash rounding booked against the selected booking, in cents.
   * Positive when the customer paid less than the exact total.
   */
  get roundingTotal(): number {
    if (!this.selectedBooking) {
      return 0;
    }
    const roundingAccountings = this.selectedBooking.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        acc.paymentMethod === PaymentMethods.METHOD_CASH_ROUNDING &&
        (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND)
    );
    return sumArray(
      roundingAccountings.flatMap((acc) =>
        acc.accountingEntriesList.map((entry) => entry.value)
      )
    );
  }

  /**
   * Checks if the current order session has no accountingEntries linked to it
   * Absence of accountingEntries linked to an order session is a sign of no pss creation nor pss cancellation
   */
  get isCurrentOrderSessionEmpty() {
    const currentOrderSessionId = this.currentOrderSessionId;
    if (!currentOrderSessionId) {
      return true;
    }

    if (!this.selectedBooking) {
      return true;
    }

    if (this.selectedBooking.accountings.length === 0) {
      return true;
    }

    const accountingEntries = this.selectedBooking.accountings
      .map((acc) => acc.accountingEntriesList)
      .flat();
    if (accountingEntries.length === 0) {
      return true;
    }

    const orderSessionsIds: string[] = accountingEntries.map(
      (accEntry) => accEntry.orderSessionId
    );
    if (orderSessionsIds.length === 0) {
      return true;
    }

    return !orderSessionsIds.includes(currentOrderSessionId);
  }

  @Mutation
  SET_CALC_VALUE(payload: string) {
    this.calculatorValue = payload;
  }

  @Mutation
  restore(bookingFields: Partial<IBookingState>) {
    const {
      bookings,
      bookingList,
      bookingsSteps,
      selectedBookingUUID,
      bookingsAmountsTrack,
      bookingsDiscountsTrack,
      promocodes,
      bookingsPendingSynchronization,
      adyenResponseStatusTrack,
      currentOrderSessionId,
      recentTabNames,
      currentBookingCount,
      bookingChanges,
    } = bookingFields;

    // keep a record of pending accountings that were marked as failed after a reload
    // it will be used to update adyenResponseStatusTrack when restoring it from local storage
    const adyenResponseStatusTrackUpdates: IAdyenResponseStatusTrack = {};

    if (bookings) {
      // after a reload, check and mark locally created pending adyen accountings as failed when restoring bookings
      // this is in order to prevent POS from confirming these accountings without a pspReference upon booking completion
      for (const uuid in bookings) {
        const booking = bookings[uuid];
        booking.accountings.forEach((accounting) => {
          if (
            accounting.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
            accounting.state === AccountingState.Pending &&
            !accounting.id
          ) {
            LogService.info(
              `Pending adyen accounting found after a reload, marking it as failed. booking: ${booking.uuid}, accounting: ${accounting.uuid}`
            );
            accounting.state = AccountingState.Failed;
            if (booking.uuid && accounting.uuid) {
              if (!adyenResponseStatusTrackUpdates[booking.uuid]) {
                // initialize if not already
                adyenResponseStatusTrackUpdates[booking.uuid] = {};
              }
              adyenResponseStatusTrackUpdates[booking.uuid][accounting.uuid] =
                AdyenResponseStatus.Aborted;
            }
          }
        });
      }
      this.bookings = bookings;
    }
    if (bookingList) {
      this.bookingList = bookingList;
    }
    if (bookingsSteps) {
      this.bookingsSteps = bookingsSteps;
    }
    if (selectedBookingUUID) {
      this.selectedBookingUUID = selectedBookingUUID;
    }
    if (bookingsAmountsTrack) {
      this.bookingsAmountsTrack = bookingsAmountsTrack;
    }

    if (bookingsDiscountsTrack) {
      this.bookingsDiscountsTrack = bookingsDiscountsTrack;
    }

    if (promocodes) {
      this.promocodes = promocodes;
    }

    if (bookingsPendingSynchronization) {
      if (Array.isArray(bookingsPendingSynchronization)) {
        // migration after changing the type from string[] to IBookingsPendingSync
        LogService.info("WARNING: unsynced bookings array not migrated", {
          bookingsPendingSynchronization,
        });
        this.bookingsPendingSynchronization = {};
      } else {
        this.bookingsPendingSynchronization = bookingsPendingSynchronization;
      }
    }

    if (adyenResponseStatusTrack) {
      // we update the adyen response status track according to the object created before restoring bookings
      for (const bookingUuid in adyenResponseStatusTrackUpdates) {
        const accountings = adyenResponseStatusTrackUpdates[bookingUuid];
        for (const accountingUuid in accountings) {
          const response = accountings[accountingUuid];
          // safety check in case of undefined properties
          if (
            adyenResponseStatusTrack[bookingUuid] &&
            adyenResponseStatusTrack[bookingUuid][accountingUuid]
          )
            adyenResponseStatusTrack[bookingUuid][accountingUuid] = response;
        }
      }
      this.adyenResponseStatusTrack = adyenResponseStatusTrack;
    }

    if (currentOrderSessionId) {
      this.currentOrderSessionId = currentOrderSessionId;
    }

    if (recentTabNames) {
      this.recentTabNames = recentTabNames;
    }

    if (currentBookingCount) {
      this.currentBookingCount = currentBookingCount;
    }

    if (bookingChanges) {
      this.bookingChanges = bookingChanges;
    }
  }

  @Mutation
  setBookingStep(payload: { bookingUUID: string; step: BookingStep }) {
    const { bookingUUID, step } = payload;

    const booking = this.bookings[bookingUUID];
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    LogService.info(
      `BookingModule: Setting step '${step}' on booking ${bookingUUID}`
    );
    this.bookings[bookingUUID].step = step;
  }

  @Mutation
  selectBookingMutation(payload: {
    bookingUUID: string;
    currentOrderSessionId: string;
    suspendBookingUUID?: string;
  }) {
    const { bookingUUID, currentOrderSessionId, suspendBookingUUID } = payload;

    if (suspendBookingUUID) {
      this.bookings[suspendBookingUUID].isSuspended = true;
    }

    this.selectedBookingUUID = bookingUUID;
    this.currentOrderSessionId = currentOrderSessionId;
  }

  @Mutation
  updateOrderSession(data: {
    bookingUuid: string;
    orderSessionId: string;
    newOrderSession: BaseOrderSession;
  }) {
    const { bookingUuid, orderSessionId, newOrderSession } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }

    const index = booking.orderSessions.findIndex(
      (orderSession) => orderSession.id === orderSessionId
    );

    if (index === -1) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }

    // Replace and update
    booking.orderSessions[index] = {
      ...newOrderSession,
      updatedAt: dayjs().toISOString(),
    };
  }

  @Mutation
  completeOrderSession(data: {
    bookingUuid: string;
    orderSessionId: string;
    state: OrderSessionState;
  }) {
    const { bookingUuid, orderSessionId, state } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    const orderSession = booking.orderSessions.find(
      (orderSession) => orderSession.id === orderSessionId
    );
    if (!orderSession) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }
    if (orderSession.state === state) {
      LogService.info("Order session already completed", { state });
    }
    const completionTime = dayjs().toISOString();
    orderSession.state = state;
    orderSession.completionDate = completionTime;
    orderSession.sessionEnd = completionTime;
    orderSession.updatedAt = completionTime;
  }

  @Mutation
  setOrderSessionHasOrderLines(data: {
    bookingUuid: string;
    orderSessionId: string;
    hasOrderLines: boolean;
  }) {
    const { bookingUuid, orderSessionId, hasOrderLines } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    const orderSession = booking.orderSessions.find(
      (orderSession) => orderSession.id === orderSessionId
    );
    if (!orderSession) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }
    orderSession.hasOrderLines = hasOrderLines;
  }

  @Mutation
  addRecentTabName(name: string) {
    const maxTabNames = 4;
    // do nothing if the name already exists in the array
    if (this.recentTabNames.includes(name)) {
      return;
    }
    // follows a LIFO behavior
    this.recentTabNames.unshift(name);
    if (this.recentTabNames.length > maxTabNames) {
      this.recentTabNames = this.recentTabNames.slice(
        0,
        this.recentTabNames.length - 1
      );
    }
  }

  @Action({ commit: "SET_CALC_VALUE", rawError: true })
  public setCalcValue(calcValue: string) {
    return calcValue;
  }
}

const BookingModule = getModule(BookingStoreModule);
export default BookingModule;
